<script lang="ts">
  //@ts-ignore
  import type { GridProps } from '@twist/core/dist/components/Grid.svelte';
  import type { ContentSourceMap, StudioPathLike } from '@sanity/svelte-loader';
  import type { Snippet } from 'svelte';
  import type {
    GridItem,
    GridLayout,
    GridItemPlacement,
    GridItemComponent,
    GridItemComponentType
  } from '$lib/sanity/schemas';

  import { TwistDivider, TwistGrid, TwistGridItem } from '@twist/core';
  import SanityComponentFactory from '$components/SanityComponents/SanityComponentFactory.svelte';
  import cloneDeep from 'lodash/cloneDeep';

  interface Props {
    sourceMap?: ContentSourceMap | undefined;
    path?: StudioPathLike;
    layout: GridLayout;
    gridProps?: GridProps;
    children?: Snippet;
  }

  let {
    sourceMap = undefined,
    path = [],
    layout,
    gridProps = {
      rowGaps: [
        ['min', 64],
        ['sm', 'lg', 64, 128],
        ['lg', 'max', 56, 112],
        ['max', 112]
      ]
    },
    children
  }: Props = $props();

  function sortByPlacement(a: TargetGridItem, b: TargetGridItem) {
    if (a.placement.rowStart < b.placement.rowStart) {
      return -1;
    }
    if (a.placement.rowStart > b.placement.rowStart) {
      return 1;
    }
    if (a.placement.columnStart < b.placement.columnStart) {
      return -1;
    }
    if (a.placement.columnStart > b.placement.columnStart) {
      return 1;
    }
    return 0;
  }

  function toTargetGridItem(item: GridItem, target: Target): TargetGridItem {
    const placement = item.placements
      ? item.placements.find((placement) => placement.grid === target)
      : item[target];

    if (!placement) {
      throw Error('gridItem should have placement properties');
    }

    return {
      component: item.component,
      componentType: item.componentType,
      placement: placement
    };
  }

  function toTargetPairs(items: GridItem[]): TargetPair[] {
    return targets.map((target) => {
      const targetItems = cloneDeep(items)
        .map((item) => toTargetGridItem(item, target))
        .sort(sortByPlacement);

      return [target, targetItems];
    });
  }
  type Target = 'desktop' | 'mobile';
  type TargetGridItem = {
    placement: GridItemPlacement;
    component: GridItemComponent;
    componentType: GridItemComponentType;
  };
  type TargetPair = [Target, TargetGridItem[]];

  const targets: Target[] = ['desktop', 'mobile'];

  let targetPairs = $derived(layout.gridItems ? toTargetPairs(layout.gridItems) : []);
</script>

{#each targetPairs as [target, items]}
  <div class={target}>
    <TwistGrid {...gridProps}>
      {@render children?.()}

      {#each items as item, index}
        <TwistGridItem
          colStart={{ min: item.placement.columnStart, lg: item.placement.columnStart }}
          colSpan={{ min: item.placement.columnSpan, lg: item.placement.columnSpan }}
          rowStart={{ min: item.placement.rowStart, lg: item.placement.rowStart }}
          rowSpan={{ min: item.placement.rowSpan, lg: item.placement.rowSpan }}
        >
          {#if item.componentType === 'divider'}
            <TwistDivider thickness="1px" margin={0} />
          {:else if item.component}
            <SanityComponentFactory
              document={item.component}
              {sourceMap}
              path={[...path, 'gridItems', index, 'component']}
              gridColspan={item.placement.columnSpan}
            />
          {/if}
        </TwistGridItem>
      {/each}
    </TwistGrid>
  </div>
{/each}

<style lang="scss">
  @import 'src/scss/media';
  @import 'src/scss/variables';

  .desktop {
    display: none;

    @include at('lg') {
      display: block;
    }
  }

  .mobile {
    @include at('lg') {
      display: none;
    }
  }
</style>
